<template>
  <div class="delivery-map">
    <CAlert v-if="mapError" color="warning" class="mb-3">
      {{ mapError }}
    </CAlert>

    <div class="delivery-map-toolbar">
      <div class="delivery-map-status">
        <div v-if="zip" class="d-flex flex-wrap align-items-center">
          <CButton
            size="sm"
            color="info"
            :disabled="zipAdded || selectedZipAlreadyLinked"
            @click="addSelectedZip"
          >
            <CIcon :name="selectedZipAlreadyLinked ? 'cil-check-alt' : 'cil-plus'" />
          </CButton>
          <span class="ml-2">
            Zip code: <strong>{{ zip }}</strong>
            <span v-if="locality">, Locality: <strong>{{ locality }}</strong></span>
            <span v-if="haversineDistance !== null"> (~{{ haversineDistance | FixedNumber }} km)</span>
            <CBadge v-if="selectedZipAlreadyLinked" color="success" class="ml-2">Already added</CBadge>
          </span>
        </div>

        <div v-else class="py-1">
          <CIcon name="cil-cursor" class="mr-1" />
          Click on the map to find a postcode, or click an amber suggested zone to add it.
        </div>

        <small v-if="mapStatus" class="text-muted d-block mt-1">{{ mapStatus }}</small>
      </div>

      <div class="delivery-map-legend">
        <span><i class="legend-dot active"></i>Active {{ activeZoneCount }}</span>
        <span><i class="legend-dot disabled"></i>Disabled {{ disabledZoneCount }}</span>
        <span><i class="legend-dot suggested"></i>Suggested {{ visibleSuggestedZones.length }}</span>
        <span v-if="warning_distance_km"><i class="legend-line warning"></i>{{ warning_distance_km | FixedNumber }} km cap</span>
        <span v-if="max_delivery_distance_km"><i class="legend-line max"></i>{{ max_delivery_distance_km | FixedNumber }} km max</span>
      </div>
    </div>

    <div ref="map" class="delivery-map-canvas"></div>
  </div>
</template>

<script>
import {
  deliveryPostcodeGroupKey,
  groupDeliveryPostcodes,
  sameDeliveryPostcodeGroup,
} from "@/helper/deliveryPostcodeGroups";

export default {
  name: "map-restaurant-delivery-areas",
  props: {
    zip_codes: { default: () => [], type: Array },
    country_id: { default: null, type: [Number, String] },
    suggested_zip_codes: { default: () => [], type: Array },
    restaurant_name: { default: null, type: String },
    restaurant_lat: { default: null, type: Number },
    restaurant_lng: { default: null, type: Number },
    warning_distance_km: { default: null, type: Number },
    max_delivery_distance_km: { default: null, type: Number },
  },
  data() {
    return {
      map: null,
      infoWindow: null,
      markers: [],
      rectangles: [],
      circles: [],
      boundaryLayer: null,
      boundaryLayerAvailable: false,
      boundaryAreasByPlaceId: {},
      boundaryGeocodeCache: {},
      lastBoundaryClickAt: null,
      zip: null,
      locality: null,
      haversineDistance: null,
      zipAdded: false,
      mapError: null,
      mapStatus: null,
      renderToken: 0,
    };
  },

  computed: {
    zipCodes: {
      get() {
        return this.zip_codes || [];
      },
      set(newValue) {
        this.$emit("update:zip_codes", newValue);
      },
    },

    suggestedZipCodes() {
      return this.suggested_zip_codes || [];
    },

    visibleSuggestedZones() {
      return this.suggestedZipCodes.filter((zone) => !this.isZipLinked(zone));
    },

    activeZoneCount() {
      return groupDeliveryPostcodes(this.zipCodes)
        .filter((zone) => !this.isDisabled(zone)).length;
    },

    disabledZoneCount() {
      return groupDeliveryPostcodes(this.zipCodes)
        .filter((zone) => this.isDisabled(zone)).length;
    },

    selectedZipAlreadyLinked() {
      const selectedZone = { zcCountryId: this.country_id, zcZip: this.zip };

      return this.zipCodes.some((zone) => sameDeliveryPostcodeGroup(zone, selectedZone));
    },

    restaurantPosition() {
      const lat = this.toNumber(this.restaurant_lat);
      const lng = this.toNumber(this.restaurant_lng);

      if (lat === null || lng === null) {
        return null;
      }

      return { lat, lng };
    },

    googleMapId() {
      const mapId = process.env.VUE_APP_GOOGLE_MAP_ID;

      return mapId && mapId !== "undefined" ? mapId : null;
    },
  },

  watch: {
    zip() {
      this.zipAdded = false;
    },

    zip_codes: {
      handler() {
        this.redrawMap();
      },
      deep: true,
    },

    suggested_zip_codes: {
      handler() {
        this.redrawMap();
      },
      deep: true,
    },

    restaurant_lat() {
      this.redrawMap();
    },

    restaurant_lng() {
      this.redrawMap();
    },

    warning_distance_km() {
      this.redrawMap();
    },

    max_delivery_distance_km() {
      this.redrawMap();
    },
  },

  mounted() {
    this.showOnMap();
  },

  beforeDestroy() {
    this.clearMap();
  },

  methods: {
    maps() {
      return window.google?.maps || null;
    },

    toNumber(value) {
      if (value === null || typeof value === "undefined" || value === "") {
        return null;
      }

      const number = Number(String(value).replace(",", "."));

      return Number.isFinite(number) ? number : null;
    },

    isDisabled(zone) {
      const value = zone?.pivot?.disabled;

      return value === true || value === 1 || value === "1" || value === "true";
    },

    sameZone(a, b) {
      return sameDeliveryPostcodeGroup(a, b);
    },

    isZipLinked(zone) {
      return this.zipCodes.some((linkedZone) => this.sameZone(linkedZone, zone));
    },

    areaPosition(area) {
      const lat = this.toNumber(area?.zcLat);
      const lng = this.toNumber(area?.zcLng);

      if (lat === null || lng === null) {
        return null;
      }

      return { lat, lng };
    },

    zoneLabel(area) {
      return String(area?.zcZip || area?.zcCity || "?").charAt(0).toUpperCase();
    },

    zoneTitle(area) {
      return `${area?.zcZip || ""} ${area?.zcCity || ""}`.trim();
    },

    stylesFor(type) {
      const styles = {
        active: {
          markerColor: "#1f9d55",
          strokeColor: "#1f9d55",
          fillColor: "#1f9d55",
          fillOpacity: 0.12,
        },
        disabled: {
          markerColor: "#8a9099",
          strokeColor: "#8a9099",
          fillColor: "#8a9099",
          fillOpacity: 0.08,
        },
        suggested: {
          markerColor: "#f59e0b",
          strokeColor: "#f59e0b",
          fillColor: "#f59e0b",
          fillOpacity: 0.18,
        },
      };

      return styles[type] || styles.active;
    },

    markerIcon(color) {
      const maps = this.maps();

      return {
        path: maps.SymbolPath.CIRCLE,
        fillColor: color,
        fillOpacity: 0.95,
        strokeColor: "#ffffff",
        strokeWeight: 2,
        scale: 8,
      };
    },

    initMap() {
      const maps = this.maps();

      if (!maps?.Map) {
        this.mapError = "Google Maps is not loaded.";
        return false;
      }

      if (!this.$refs.map) {
        return false;
      }

      if (this.map) {
        return true;
      }

      this.mapError = null;
      const mapOptions = {
        zoom: 10,
        draggableCursor: "crosshair",
        mapTypeControl: false,
        streetViewControl: false,
        fullscreenControl: true,
      };

      if (this.googleMapId) {
        mapOptions.mapId = this.googleMapId;
      }

      this.map = new maps.Map(this.$refs.map, mapOptions);
      this.infoWindow = new maps.InfoWindow();
      this.map.addListener("click", this.handleMapClick);
      this.initBoundaryLayer();

      return true;
    },

    initBoundaryLayer() {
      if (!this.googleMapId || !this.map?.getFeatureLayer) {
        return;
      }

      try {
        this.boundaryLayer = this.map.getFeatureLayer("POSTAL_CODE");
      } catch (error) {
        this.boundaryLayer = null;
        return;
      }

      if (!this.boundaryLayer) {
        return;
      }

      this.boundaryLayer.addListener("click", this.handleBoundaryClick);
      this.map.addListener("mapcapabilities_changed", () => {
        const wasAvailable = this.boundaryLayerAvailable;

        this.updateBoundaryLayerAvailability();

        if (wasAvailable !== this.boundaryLayerAvailable) {
          this.redrawMap();
        }
      });
      this.updateBoundaryLayerAvailability();
    },

    updateBoundaryLayerAvailability() {
      const capabilities = this.map?.getMapCapabilities?.();
      const available = Boolean(
        this.boundaryLayer &&
        capabilities?.isDataDrivenStylingAvailable
      );

      this.boundaryLayerAvailable = available;

      if (!available) {
        this.clearBoundaryStyles();
      }

      return available;
    },

    showOnMap() {
      if (!this.initMap()) {
        return;
      }

      this.redrawMap();

      window.setTimeout(() => {
        const maps = this.maps();

        if (maps?.event && this.map) {
          maps.event.trigger(this.map, "resize");
          this.fitMapToContent();
        }
      }, 250);
    },

    clearMap() {
      this.renderToken += 1;
      this.clearMarkers();
      this.clearRectangles();
      this.clearCircles();
      this.clearBoundaryStyles();

      if (this.infoWindow) {
        this.infoWindow.close();
      }
    },

    clearMarkers() {
      this.markers.forEach((marker) => marker.setMap(null));
      this.markers = [];
    },

    clearRectangles() {
      this.rectangles.forEach((rectangle) => rectangle.setMap(null));
      this.rectangles = [];
    },

    clearCircles() {
      this.circles.forEach((circle) => circle.setMap(null));
      this.circles = [];
    },

    clearBoundaryStyles() {
      this.boundaryAreasByPlaceId = {};

      if (this.boundaryLayer) {
        this.boundaryLayer.style = () => null;
      }
    },

    redrawMap() {
      if (!this.map && !this.initMap()) {
        return;
      }

      this.updateBoundaryLayerAvailability();

      const maps = this.maps();
      const bounds = new maps.LatLngBounds();
      const token = this.renderToken + 1;

      this.renderToken = token;
      this.clearMarkers();
      this.clearRectangles();
      this.clearCircles();
      this.clearBoundaryStyles();

      this.addRestaurantMarker(bounds);
      this.addDistanceCircles(bounds);

      this.zipCodes.forEach((area) => {
        this.drawArea(area, this.isDisabled(area) ? "disabled" : "active", true, bounds, token);
      });

      this.visibleSuggestedZones.forEach((area) => {
        this.drawArea(area, "suggested", false, bounds, token);
      });

      this.fitMapToBounds(bounds);
    },

    addRestaurantMarker(bounds) {
      if (!this.restaurantPosition) {
        this.mapStatus = "Restaurant coordinates are missing.";
        return;
      }

      const maps = this.maps();
      const marker = new maps.Marker({
        map: this.map,
        position: this.restaurantPosition,
        label: String(this.restaurant_name || "R").charAt(0).toUpperCase(),
        title: this.restaurant_name || "Restaurant",
        icon: "/img/map/restaurant.png",
      });

      this.markers.push(marker);
      bounds.extend(this.restaurantPosition);
    },

    addDistanceCircles(bounds) {
      if (!this.restaurantPosition) {
        return;
      }

      if (this.warning_distance_km) {
        this.addDistanceCircle(this.warning_distance_km, "#f59e0b", "Warning cap", bounds);
      }

      if (this.max_delivery_distance_km) {
        this.addDistanceCircle(this.max_delivery_distance_km, "#6f42c1", "Maximum delivery distance", bounds);
      }
    },

    addDistanceCircle(distanceKm, color, title, bounds) {
      const maps = this.maps();
      const radius = this.toNumber(distanceKm) * 1000;

      if (!radius) {
        return;
      }

      const circle = new maps.Circle({
        map: this.map,
        center: this.restaurantPosition,
        radius,
        strokeColor: color,
        strokeOpacity: 0.85,
        strokeWeight: 2,
        fillColor: color,
        fillOpacity: 0.035,
        clickable: false,
      });

      circle.title = title;
      this.circles.push(circle);

      const circleBounds = circle.getBounds();
      if (circleBounds) {
        this.extendBounds(bounds, circleBounds);
      }
    },

    drawArea(area, type, persistBounds, bounds, token) {
      const position = this.areaPosition(area);
      const styles = this.stylesFor(type);

      if (position) {
        this.addZoneMarker(area, type, styles, position);
        bounds.extend(position);
      }

      if (this.boundaryLayerAvailable) {
        this.drawAreaBoundary(area, type, styles, persistBounds, bounds, token);
        return;
      }

      this.drawAreaRectangle(area, type, styles, persistBounds, bounds, token);
    },

    drawAreaRectangle(area, type, styles, persistBounds, bounds, token) {
      const literalBounds = this.normalizeBounds(area.bounds);
      if (literalBounds) {
        this.addZoneRectangle(area, type, styles, literalBounds);
        this.extendBounds(bounds, literalBounds);
        this.fitMapToBounds(bounds);
        return;
      }

      this.geocodeArea(area, persistBounds)
        .then((geocodedArea) => {
          if (token !== this.renderToken || !geocodedArea?.bounds) {
            return;
          }

          this.addZoneRectangle(area, type, styles, geocodedArea.bounds);
          this.extendBounds(bounds, geocodedArea.bounds);
          this.fitMapToBounds(bounds);
        });
    },

    drawAreaBoundary(area, type, styles, persistBounds, bounds, token) {
      const literalBounds = this.normalizeBounds(area.bounds);

      if (literalBounds) {
        this.extendBounds(bounds, literalBounds);
        this.fitMapToBounds(bounds);
      }

      this.geocodeArea(area, persistBounds && !literalBounds)
        .then((geocodedArea) => {
          if (token !== this.renderToken) {
            return;
          }

          const geocodedBounds = geocodedArea?.bounds;
          const fallbackBounds = literalBounds || geocodedBounds;

          if (geocodedBounds && !literalBounds) {
            this.extendBounds(bounds, geocodedBounds);
            this.fitMapToBounds(bounds);
          }

          if (geocodedArea?.placeId) {
            this.addZoneBoundary(geocodedArea.placeId, area, type, styles);
            return;
          }

          if (fallbackBounds) {
            this.addZoneRectangle(area, type, styles, fallbackBounds);
          }
        });
    },

    addZoneMarker(area, type, styles, position) {
      const maps = this.maps();
      const marker = new maps.Marker({
        map: this.map,
        position,
        label: this.zoneLabel(area),
        title: type === "suggested"
          ? `${this.zoneTitle(area)} - click to add`
          : this.zoneTitle(area),
        icon: this.markerIcon(styles.markerColor),
      });

      marker.addListener("click", () => this.handleAreaClick(area, type, position));
      this.markers.push(marker);
    },

    addZoneRectangle(area, type, styles, bounds) {
      const maps = this.maps();
      const rectangle = new maps.Rectangle({
        map: this.map,
        bounds,
        strokeColor: styles.strokeColor,
        strokeOpacity: 0.75,
        strokeWeight: type === "suggested" ? 2 : 1,
        fillColor: styles.fillColor,
        fillOpacity: styles.fillOpacity,
      });

      rectangle.addListener("click", () => this.handleAreaClick(area, type, rectangle.getBounds().getCenter()));
      this.rectangles.push(rectangle);
    },

    addZoneBoundary(placeId, area, type, styles) {
      this.boundaryAreasByPlaceId = {
        ...this.boundaryAreasByPlaceId,
        [placeId]: { area, type, styles },
      };

      this.applyBoundaryStyles();
    },

    applyBoundaryStyles() {
      if (!this.boundaryLayer || !this.boundaryLayerAvailable) {
        return;
      }

      const areasByPlaceId = { ...this.boundaryAreasByPlaceId };

      this.boundaryLayer.style = ({ feature }) => {
        const entry = areasByPlaceId[feature.placeId];

        if (!entry) {
          return null;
        }

        return {
          strokeColor: entry.styles.strokeColor,
          strokeOpacity: 0.75,
          strokeWeight: entry.type === "suggested" ? 2 : 1,
          fillColor: entry.styles.fillColor,
          fillOpacity: Math.max(entry.styles.fillOpacity, 0.1),
        };
      };
    },

    handleBoundaryClick(event) {
      const feature = event?.features?.find((item) => (
        this.boundaryAreasByPlaceId[item.placeId]
      ));

      if (!feature) {
        return;
      }

      const entry = this.boundaryAreasByPlaceId[feature.placeId];
      const position = event.latLng || this.areaPosition(entry.area);

      this.lastBoundaryClickAt = Date.now();
      this.handleAreaClick(entry.area, entry.type, position);
    },

    handleAreaClick(area, type, position) {
      if (type === "suggested") {
        this.addZip(area.zcZip, area.zcCountryId);
        return;
      }

      if (!this.infoWindow) {
        return;
      }

      const status = this.isDisabled(area) ? "Disabled" : "Active";
      this.infoWindow.setContent(`
        <div class="delivery-map-info">
          <strong>${area.zcZip || ""} ${area.zcCity || ""}</strong><br>
          ${status}
        </div>
      `);
      this.infoWindow.setPosition(position);
      this.infoWindow.open(this.map);
    },

    normalizeBounds(bounds) {
      if (!bounds) {
        return null;
      }

      if (bounds.getNorthEast && bounds.getSouthWest) {
        return bounds;
      }

      const north = this.toNumber(bounds.north);
      const south = this.toNumber(bounds.south);
      const east = this.toNumber(bounds.east);
      const west = this.toNumber(bounds.west);

      if ([north, south, east, west].some((value) => value === null)) {
        return null;
      }

      return { north, south, east, west };
    },

    extendBounds(mapBounds, areaBounds) {
      if (areaBounds.getNorthEast && areaBounds.getSouthWest) {
        mapBounds.extend(areaBounds.getNorthEast());
        mapBounds.extend(areaBounds.getSouthWest());
        return;
      }

      mapBounds.extend({ lat: areaBounds.north, lng: areaBounds.east });
      mapBounds.extend({ lat: areaBounds.south, lng: areaBounds.west });
    },

    fitMapToContent() {
      if (!this.map) {
        return;
      }

      const maps = this.maps();
      const bounds = new maps.LatLngBounds();

      if (this.restaurantPosition) {
        bounds.extend(this.restaurantPosition);
      }

      this.markers.forEach((marker) => {
        const position = marker.getPosition();
        if (position) {
          bounds.extend(position);
        }
      });

      this.rectangles.forEach((rectangle) => {
        const rectangleBounds = rectangle.getBounds();
        if (rectangleBounds) {
          this.extendBounds(bounds, rectangleBounds);
        }
      });

      this.circles.forEach((circle) => {
        const circleBounds = circle.getBounds();
        if (circleBounds) {
          this.extendBounds(bounds, circleBounds);
        }
      });

      this.fitMapToBounds(bounds);
    },

    fitMapToBounds(bounds) {
      if (!this.map || bounds.isEmpty()) {
        return;
      }

      this.map.fitBounds(bounds);

      if (!this.circles.length && this.zipCodes.length + this.visibleSuggestedZones.length === 0 && this.restaurantPosition) {
        this.map.setCenter(this.restaurantPosition);
        this.map.setZoom(12);
      }
    },

    zoneKey(area) {
      return deliveryPostcodeGroupKey(area);
    },

    geocodeArea(area, persistBounds) {
      const key = this.zoneKey(area);

      if (!this.boundaryGeocodeCache[key]) {
        this.boundaryGeocodeCache[key] = this.fetchGeocodeArea(area);
      }

      return this.boundaryGeocodeCache[key].then((geocodedArea) => {
        if (persistBounds && geocodedArea?.bounds) {
          this.saveBounds(area.id, geocodedArea.bounds);
        }

        return geocodedArea;
      });
    },

    fetchGeocodeArea(area) {
      const maps = this.maps();

      if (!maps?.Geocoder) {
        return Promise.resolve(null);
      }

      const country = this.authCountries?.find((item) => item.value === area.zcCountryId);
      const geocoder = new maps.Geocoder();

      return new Promise((resolve) => {
        geocoder.geocode({
          componentRestrictions: {
            country: country ? country.iso2 : "BE",
            postalCode: area.zcZip,
          },
        }, (results, status) => {
          if (status !== maps.GeocoderStatus.OK || !results[0]) {
            resolve(null);
            return;
          }

          const result = this.pickPostalCodeResult(results) || results[0];
          const bounds = result.geometry?.bounds;

          resolve({
            bounds: bounds ? bounds.toJSON() : null,
            placeId: result.place_id || null,
          });
        });
      });
    },

    pickPostalCodeResult(results) {
      return results.find((result) => result.types?.includes("postal_code"));
    },

    async saveBounds(zipCodeId, bounds) {
      if (!zipCodeId) {
        return;
      }

      await this.$axios
        .post(this.$backend.ZIP_CODES.STORE_ZIP_CODE_BOUNDS.replace("{zip_code_id}", zipCodeId), bounds)
        .then((response) => {
          this.zipCodes = this.zipCodes.map((zone) => (
            zone.id === zipCodeId
              ? { ...zone, bounds: response.data }
              : zone
          ));
        });
    },

    handleMapClick(event) {
      if (this.lastBoundaryClickAt && Date.now() - this.lastBoundaryClickAt < 250) {
        return;
      }

      const maps = this.maps();

      if (!maps?.Geocoder) {
        this.mapStatus = "Google Maps geocoder is not loaded.";
        return;
      }

      this.zip = null;
      this.locality = null;
      this.haversineDistance = null;
      this.mapStatus = "Looking up postcode...";

      const geocoder = new maps.Geocoder();
      geocoder.geocode({ latLng: event.latLng }, (results, status) => {
        if (status !== maps.GeocoderStatus.OK || !results[0]) {
          this.mapStatus = `Geocoding failed with status ${status}`;
          return;
        }

        const postalCode = this.addressComponent(results[0], "postal_code");
        const locality = this.addressComponent(results[0], "locality");

        if (!postalCode) {
          this.mapStatus = "No postcode found for this location.";
          return;
        }

        this.zip = postalCode;
        this.locality = locality;
        this.haversineDistance = this.restaurantPosition
          ? this.$globalFunc.haversineDistance(this.restaurantPosition, {
              lat: event.latLng.lat(),
              lng: event.latLng.lng(),
            })
          : null;
        this.mapStatus = this.selectedZipAlreadyLinked
          ? `${postalCode} is already linked.`
          : "Postcode found. Use the plus button to add it.";
      });
    },

    addressComponent(result, componentType) {
      const component = result.address_components.find((item) => item.types.includes(componentType));

      return component?.long_name || null;
    },

    addSelectedZip() {
      this.addZip(this.zip, this.country_id);
    },

    addZip(zip, countryId = this.country_id) {
      if (!zip) {
        return;
      }

      const selectedZone = { zcCountryId: countryId, zcZip: zip };

      if (this.zipCodes.some((zone) => sameDeliveryPostcodeGroup(zone, selectedZone))) {
        this.zipAdded = true;
        this.mapStatus = `${zip} is already linked.`;
        return;
      }

      this.$emit("add:zip", zip, null, countryId);
      this.zipAdded = true;
      this.mapStatus = `${zip} added to delivery zones.`;
    },
  },
};
</script>

<style scoped>
.delivery-map {
  min-height: 640px;
}

.delivery-map-toolbar {
  align-items: flex-start;
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem;
  justify-content: space-between;
  margin-bottom: 0.75rem;
}

.delivery-map-status {
  min-width: 280px;
}

.delivery-map-legend {
  align-items: center;
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem;
  justify-content: flex-end;
}

.delivery-map-legend span {
  align-items: center;
  display: inline-flex;
  font-size: 0.85rem;
  white-space: nowrap;
}

.legend-dot {
  border-radius: 999px;
  display: inline-block;
  height: 10px;
  margin-right: 0.35rem;
  width: 10px;
}

.legend-dot.active {
  background: #1f9d55;
}

.legend-dot.disabled {
  background: #8a9099;
}

.legend-dot.suggested {
  background: #f59e0b;
}

.legend-line {
  border-top: 2px solid;
  display: inline-block;
  margin-right: 0.35rem;
  width: 18px;
}

.legend-line.warning {
  border-color: #f59e0b;
}

.legend-line.max {
  border-color: #6f42c1;
}

.delivery-map-canvas {
  border: 1px solid #d8dbe0;
  height: 600px;
  width: 100%;
}
</style>
